import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { ref, onValue, off, get } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";

const glassCardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(18px)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

function formatBRL(n) {
  return Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function pct(a, b) {
  if (!b) return 0;
  return Math.round((a / b) * 100);
}

function formatShortDate(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const organizerId = user?.uid || null;

  const [eventsRaw, setEventsRaw] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizerId) {
      setLoading(false);
      return;
    }

    const eventsRef = ref(rtdb, "events");
    const handler = (snap) => {
      const val = snap.val();
      const list = val
        ? Object.entries(val)
            .map(([id, data]) => ({ id, ...(data || {}) }))
            .filter((e) => e.organizerId === organizerId)
        : [];
      setEventsRaw(list);
      setLoading(false);
    };

    onValue(eventsRef, handler, () => {
      setEventsRaw([]);
      setLoading(false);
    });

    return () => off(eventsRef, "value", handler);
  }, [organizerId]);

  // carrega TODOS os pedidos dos eventos do organizador
  useEffect(() => {
    if (!organizerId || eventsRaw.length === 0) return;

    const eventIds = new Set(eventsRaw.map((e) => e.id));
    get(ref(rtdb, "orders")).then((snap) => {
      if (!snap.exists()) { setAllOrders([]); return; }
      const mine = Object.values(snap.val() || {})
        .filter((o) => eventIds.has(o.eventId))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setAllOrders(mine);
    }).catch(() => { setAllOrders([]); });
  }, [eventsRaw, organizerId]);

  const now = new Date();

  // contagem de ingressos vendidos por evento (a partir dos pedidos reais)
  const soldByEvent = useMemo(() => {
    const map = {};
    for (const o of allOrders) {
      map[o.eventId] = (map[o.eventId] || 0) + Number(o.ticketCount || 0);
    }
    return map;
  }, [allOrders]);

  const upcomingEvents = useMemo(() => {
    return eventsRaw
      .filter((e) => e.status !== "cancelled")
      .map((e) => {
        const startMs = e.timing?.startAtMs ?? e.startAtMs ?? null;
        return {
          id: e.id,
          name: e.title || "Sem título",
          date: startMs ? formatShortDate(startMs) : "Sem data",
          venue: e.location?.name || e.location?.city || "—",
          status: e.status === "online" ? "Publicado" : "Rascunho",
          sold: soldByEvent[e.id] || 0,
          startMs,
        };
      })
      .sort((a, b) => {
        if (!a.startMs) return 1;
        if (!b.startMs) return -1;
        return Number(a.startMs) - Number(b.startMs);
      })
      .slice(0, 5);
  }, [eventsRaw, soldByEvent]);

  const kpis = useMemo(() => {
    const published = eventsRaw.filter((e) => e.status === "online").length;
    // derivados dos pedidos reais (não de events/stats, que pode ficar desatualizado)
    const totalRevenue = allOrders.reduce((acc, o) => acc + Number(o.summary?.total || 0), 0);
    const ticketsSold = allOrders.reduce((acc, o) => acc + Number(o.ticketCount || 0), 0);
    return { published, totalRevenue, ticketsSold };
  }, [eventsRaw, allOrders]);

  const alerts = useMemo(() => {
    const list = [];
    eventsRaw.forEach((e) => {
      if (e.status === "draft" && !e.stats?.ticketTypesCount) {
        list.push({
          title: `"${e.title}" em rascunho sem ingressos`,
          desc: "Configure os ingressos para publicar.",
          actionLabel: "Resolver",
          actionTo: `/dashboard/eventos/${e.id}`,
        });
      }
    });
    return list.slice(0, 3);
  }, [eventsRaw]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)" }}>
            VISÃO GERAL •{" "}
            {now.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, color: "rgba(255,255,255,0.95)" }}
          >
            Dashboard
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => navigate("/dashboard/eventos/novo")}
            sx={{ borderRadius: "12px", fontWeight: 700 }}
          >
            Criar evento
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 2.5,
        }}
      >
        {/* KPI 1: Receita */}
        <Paper sx={glassCardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Receita total
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {formatBRL(kpis.totalRevenue)}
              </Typography>
            </Stack>
            <Avatar sx={{ bgcolor: "rgba(0,180,255,0.15)", color: "#40c4ff", width: 40, height: 40 }}>
              <TrendingUpRoundedIcon />
            </Avatar>
          </Stack>
        </Paper>

        {/* KPI 2: Ingressos */}
        <Paper sx={glassCardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Ingressos vendidos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {kpis.ticketsSold}
              </Typography>
            </Stack>
            <Avatar sx={{ bgcolor: "rgba(0,255,180,0.15)", color: "#00e6a0", width: 40, height: 40 }}>
              <ConfirmationNumberRoundedIcon />
            </Avatar>
          </Stack>
        </Paper>

        {/* KPI 3: Eventos ativos */}
        <Paper sx={glassCardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Eventos publicados
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {kpis.published}
              </Typography>
            </Stack>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff", width: 40, height: 40 }}>
              <EventRoundedIcon />
            </Avatar>
          </Stack>
        </Paper>

        {/* KPI 4: Total de eventos */}
        <Paper sx={glassCardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Total de eventos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {eventsRaw.length}
              </Typography>
            </Stack>
            <Avatar sx={{ bgcolor: "rgba(255,200,0,0.15)", color: "#ffd54f", width: 40, height: 40 }}>
              <EventRoundedIcon />
            </Avatar>
          </Stack>
        </Paper>

        {/* Próximos Eventos */}
        <Paper
          sx={{
            ...glassCardSx,
            gridColumn: { xs: "span 1", md: "span 2" },
            gridRow: { xs: "auto", md: "span 2" },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Próximos Eventos
            </Typography>
            <Button
              size="small"
              sx={{ color: "rgba(255,255,255,0.6)" }}
              onClick={() => navigate("/dashboard/eventos")}
            >
              Ver todos
            </Button>
          </Stack>

          {loading ? (
            <Typography sx={{ opacity: 0.5, fontSize: 14 }}>Carregando...</Typography>
          ) : upcomingEvents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography sx={{ opacity: 0.6, fontSize: 14, mb: 1.5 }}>
                Nenhum evento cadastrado ainda.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/dashboard/eventos/novo")}
                sx={{ borderRadius: "10px" }}
              >
                Criar primeiro evento
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ flex: 1 }}>
              {upcomingEvents.map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                        {e.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                        {e.date}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={e.status}
                      sx={{
                        height: 24,
                        fontSize: "0.7rem",
                        bgcolor:
                          e.status === "Publicado"
                            ? "rgba(0,255,180,0.1)"
                            : "rgba(255,255,255,0.1)",
                        color: e.status === "Publicado" ? "#69f0ae" : "#fff",
                      }}
                    />
                  </Stack>
                  {e.sold > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, e.sold)}
                        sx={{ flex: 1, height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.1)" }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>
                        {e.sold} vendidos
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Vendas Recentes (placeholder — sem coleção de vendas ainda) */}
        <Paper
          sx={{
            ...glassCardSx,
            gridColumn: { xs: "span 1", md: "span 2" },
            gridRow: { xs: "auto", md: "span 2" },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Vendas Recentes
            </Typography>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "rgba(255,255,255,0.5)", pl: 0 }}>Comprador</TableCell>
                <TableCell sx={{ color: "rgba(255,255,255,0.5)" }}>Evento</TableCell>
                <TableCell align="right" sx={{ color: "rgba(255,255,255,0.5)", pr: 0 }}>Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center", opacity: 0.5, color: "white", py: 3, border: "none" }}>
                    As vendas aparecerão aqui assim que forem realizadas.
                  </TableCell>
                </TableRow>
              ) : (
                allOrders.slice(0, 5).map((o) => (
                  <TableRow key={o.orderId} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ color: "white", pl: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.buyer?.name || "—"}</Typography>
                      <Typography sx={{ fontSize: 11, opacity: 0.5 }}>{o.ticketCount} ingresso{o.ticketCount !== 1 ? "s" : ""}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.eventTitle || "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0, color: "#69f0ae", fontWeight: 900, whiteSpace: "nowrap" }}>
                      {formatBRL(o.summary?.total)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Alertas */}
        <Paper sx={{ ...glassCardSx, gridColumn: { xs: "span 1", md: "span 2" } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <WarningAmberRoundedIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Alertas
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {alerts.length === 0 ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
                Tudo certo por aqui.
              </Typography>
            ) : (
              alerts.map((alert, i) => (
                <Stack
                  key={i}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: "rgba(255,160,0,0.08)",
                    border: "1px solid rgba(255,160,0,0.15)",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#ffcc80" }}>
                      {alert.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                      {alert.desc}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => navigate(alert.actionTo)}>
                    <ArrowForwardRoundedIcon sx={{ color: "#ffcc80" }} />
                  </IconButton>
                </Stack>
              ))
            )}
          </Stack>
        </Paper>

        {/* Ações Rápidas */}
        <Paper sx={{ ...glassCardSx, gridColumn: { xs: "span 1", md: "span 1" } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
            Ações
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScannerRoundedIcon />}
              fullWidth
              onClick={() => navigate("/checkin")}
              sx={{ justifyContent: "flex-start", color: "white", borderColor: "rgba(255,255,255,0.2)" }}
            >
              Scanner
            </Button>
          </Stack>
        </Paper>

        {/* Resumo de receita */}
        <Paper sx={{ ...glassCardSx, gridColumn: { xs: "span 1", md: "span 1" } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Receita
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, color: "#fff" }}>
            {formatBRL(kpis.totalRevenue)}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.5 }}>
            {kpis.ticketsSold} ingressos vendidos
          </Typography>
          <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,0.1)" }} />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            {kpis.published} evento{kpis.published !== 1 ? "s" : ""} publicado{kpis.published !== 1 ? "s" : ""}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
